// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './CompressionReceived.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { createAxios } from 'ts/createAxios';
import { LabelContents } from 'components/molecules/LabelContents';
import { CheckBoxCard } from 'components/molecules/CheckBoxCard';
import { IdType } from 'util/Enums';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { useTranslation } from 'react-i18next';
import { BottomField } from 'components/molecules/BottomField';
import { DATE_FORMAT } from 'util/ConstantValues';
import { toast } from 'react-toastify';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { EdiStatus, Option } from 'util/Types';
import WarningModal from 'components/molecules/WarningModal';
import { IconFilter } from 'components/atoms/icons/IconFilter';
import { SelectField } from 'components/molecules/SelectField';
import { uniqBy } from 'lodash';
import { API_ENDPOINTS } from 'util/endPoints';

export type Receive = {
  deliveryId: number | null;
  deliveryFromAreaName: string;
  deliveriedDatetime: string;
  tenantName: string;
  weight: number;
  targetType: string;
  description: string;
  targetIds: string[];
  deliveryFromSiteName: string;
  ediNumber?: string;
  ediStatus?: EdiStatus;
};

export function CompressionReceived({ active }: { active: boolean }) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const api = createAxios();
  const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);
  const [receives, setReceives] = useState<Receive[]>([]);
  const [filterReceives, setfilterReceives] = useState<Receive[]>([]);
  const [alertEdiNumber, setAlertEdiNumber] = useState<boolean>(false);

  const materialOptions = useMemo((): Option[] => {
    const options =
      receives.map((item) => ({
        label: item.description,
        value: item.description,
      })) || [];

    return uniqBy(options, 'value');
  }, [receives]);

  const handleChange = (index: number) => {
    if (checkedIndexes.includes(index)) {
      setCheckedIndexes(checkedIndexes.filter((i) => i !== index));
    } else {
      setCheckedIndexes([...checkedIndexes, index]);
    }
  };

  const callGetReceivesApi = async () => {
    const res = await api.get('/compressions/receives');
    setReceives(res.data);
    setfilterReceives(res.data);
  };

  useEffect(() => {
    if (active) callGetReceivesApi();
  }, [active]);

  const submit = async () => {
    const checkedItems = filterReceives.filter((_, i) =>
      checkedIndexes.includes(i)
    );

    const selectedDuplicatedMaterialNames =
      checkedItems.length > 0 &&
      !checkedItems.every(
        (item, _, array) => item.description === array[0].description
      );

    if (selectedDuplicatedMaterialNames) {
      toast.error(t('collect_registration.submit_error_material_name'));

      return;
    }

    if (
      checkedItems.some(
        ({ ediNumber, ediStatus }) =>
          !ediNumber && ediStatus != null && ediStatus !== EdiStatus.None
      ) &&
      !alertEdiNumber
    ) {
      setAlertEdiNumber(true);

      return;
    }

    setAlertEdiNumber(false);

    api
      .post(API_ENDPOINTS.COMPRESSION_RECEIVES, {
        deliveryIds: checkedItems
          .map((item) => item.deliveryId?.toString())
          .filter(Boolean),
        resourceIds: checkedItems
          .filter((item) => item.deliveryId == null)
          .flatMap((item) => item.targetIds),
      })
      .then(() => {
        toast.success(t('messages.M_135'));
        setCheckedIndexes([]);
        callGetReceivesApi();
      })
      .catch((e) => {
        toast.error(
          e?.response?.data?.message
            ? e.response.data.message
            : t('receives.signup_messages.failed')
        );
      });
  };

  const handleCheckAll = () => {
    if (checkedIndexes.length === filterReceives.length) {
      setCheckedIndexes([]);
    } else {
      setCheckedIndexes(filterReceives.map((_, i) => i));
    }
  };

  const handleFilter = (marterialName: string) => {
    const filter = receives.filter((item) => item.description == marterialName);
    setfilterReceives(filter.length > 0 ? filter : receives);
    setCheckedIndexes([]);
  };

  const formatSiteName = (item: Receive) => {
    if (item.tenantName) {
      return `${item.tenantName}（${item.deliveryFromSiteName}）`;
    }

    return item.deliveryFromSiteName;
  };

  return (
    <div className={styles.compressionReceived}>
      <Form<Receive> onSubmit={() => {}} keepDirtyOnReinitialize>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.topSelect}>
              <div className={styles.checkBox} onClick={handleCheckAll}>
                {t('common.collective_selection')}&nbsp;
              </div>
              <div className={styles.content}>
                <div className={styles.label}>{t('common.content')}</div>
                <div className={styles.materialName}>
                  <Form onSubmit={() => {}}>
                    {(props) => {
                      return (
                        <form onSubmit={props.handleSubmit}>
                          <div
                            className={mergeClasses(styles.selectName, {
                              ...(language && {
                                [styles[language]]: true,
                              }),
                            })}
                          >
                            <div className={styles.iconFilter}>
                              <IconFilter />
                            </div>

                            <SelectField
                              name="materialName"
                              placeholder={t('common.material_name')}
                              options={[
                                {
                                  label: t(
                                    'purchase_page.receive.select_material_name'
                                  ),
                                  value: '',
                                },
                                ...materialOptions,
                              ]}
                              onChange={(value) =>
                                handleFilter(value.toString())
                              }
                              isDarkmode
                              showPlaceholderOption
                            />
                          </div>
                        </form>
                      );
                    }}
                  </Form>
                </div>
              </div>
            </div>
            <div className="scroll-contents">
              {filterReceives.length > 0 ? (
                filterReceives.map((item, i) => {
                  return (
                    <CheckBoxCard
                      key={i}
                      id={`receive-item-${i}`}
                      checked={checkedIndexes.includes(i)}
                      onChange={() => handleChange(i)}
                    >
                      <LabelContents
                        label={t('common.date_time')}
                        contents={getFormatDateWithCurrentTimezone(
                          item.deliveriedDatetime,
                          DATE_FORMAT.slaYMDHm
                        )}
                      />
                      <LabelContents
                        label={t('common.place')}
                        contents={formatSiteName(item)}
                      />
                      <LabelContents
                        label={t('common.material_name')}
                        contents={item.description}
                      />
                      <LabelContents
                        label={t('common.weight')}
                        contents={`${normalizeNumber({
                          value: item.weight,
                        })}kg`}
                      />
                      <LabelContents
                        label={t(`receives.id_type.${IdType[item.targetType]}`)}
                        contents={item.targetIds.join('\n')}
                      />
                    </CheckBoxCard>
                  );
                })
              ) : (
                <div className="no-data">{t('messages.M_008')}</div>
              )}
            </div>
            <BottomField>
              <div className={styles.bottomBtn}>
                <button
                  className={styles.btnRequest}
                  type="button"
                  disabled={!checkedIndexes.length}
                  onClick={submit}
                >
                  {t('common.receive')}
                </button>
              </div>
            </BottomField>

            {alertEdiNumber && (
              <WarningModal
                isDarkMode
                title={t('common.alert_no_edi_manifest_number.title')}
                content={t('common.alert_no_edi_manifest_number.content')}
                buttonConfirm={t('common.receive')}
                onClose={() => {
                  setAlertEdiNumber(false);
                }}
                onConfirm={submit}
              />
            )}
          </form>
        )}
      </Form>
    </div>
  );
}
